"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import type { ApiProfileMin } from "@/generated/models/ApiProfileMin";
import { getSubmissionCount, getTrophyArtworkCount } from "@/helpers/artist-activity.helpers";
import { useArtistPreviewModal, type ArtistPreviewTab } from "@/hooks/useArtistPreviewModal";
import { useWaveCreatorPreviewModal } from "@/hooks/useWaveCreatorPreviewModal";
import { closeAllCustomTooltips } from "@/helpers/tooltip.helpers";
import { ArtistActivityBadge } from "./ArtistActivityBadge";
import { ArtistPreviewModal } from "./ArtistPreviewModal";
import { WaveCreatorBadge } from "./WaveCreatorBadge";
import { WaveCreatorPreviewModal } from "./WaveCreatorPreviewModal";
interface DropAuthorBadgesProfile {
  readonly id?: string | null;
  readonly handle?: string | null;
  readonly pfp?: string | null;
  readonly banner1_color?: string | null;
  readonly banner2_color?: string | null;
  readonly cic?: number;
  readonly rep?: number;
  readonly tdh?: number;
  readonly tdh_rate?: number;
  readonly xtdh?: number;
  readonly xtdh_rate?: number;
  readonly level?: number;
  readonly primary_address?: string | null;
  readonly primary_wallet?: string | null;
  readonly archived?: boolean;
  readonly active_main_stage_submission_ids?: readonly string[] | null;
  readonly winner_main_stage_drop_ids?: readonly string[] | null;
  readonly artist_of_prevote_cards?: readonly number[] | null;
  readonly is_wave_creator?: boolean | null;
}
interface DropAuthorBadgesProps {
  readonly profile: DropAuthorBadgesProfile;
  readonly tooltipIdPrefix?: string | undefined;
  readonly className?: string | undefined;
  readonly onArtistPreviewOpen?: ((params: {
    readonly user: ApiProfileMin;
    readonly initialTab: ArtistPreviewTab;
  }) => void) | undefined;
  readonly onWaveCreatorPreviewOpen?: ((user: ApiProfileMin) => void) | undefined;
}
const DEFAULT_CONTAINER_CLASS = "tw-inline-flex tw-items-center tw-gap-x-1";
const toApiProfileMin = (profile: DropAuthorBadgesProfile): ApiProfileMin => {
  const primaryAddress = profile.primary_address ?? profile.primary_wallet ?? "";
  const fallbackId = primaryAddress;
  return {
    id: profile.id ?? fallbackId,
    handle: profile.handle ?? null,
    pfp: profile.pfp ?? null,
    banner1_color: profile.banner1_color ?? null,
    banner2_color: profile.banner2_color ?? null,
    cic: profile.cic ?? 0,
    rep: profile.rep ?? 0,
    tdh: profile.tdh ?? 0,
    tdh_rate: profile.tdh_rate ?? 0,
    xtdh: profile.xtdh ?? 0,
    xtdh_rate: profile.xtdh_rate ?? 0,
    level: profile.level ?? 0,
    primary_address: primaryAddress,
    subscribed_actions: [],
    archived: profile.archived ?? false,
    active_main_stage_submission_ids: profile.active_main_stage_submission_ids !== null && profile.active_main_stage_submission_ids !== undefined ? [...profile.active_main_stage_submission_ids] : [],
    winner_main_stage_drop_ids: profile.winner_main_stage_drop_ids !== null && profile.winner_main_stage_drop_ids !== undefined ? [...profile.winner_main_stage_drop_ids] : [],
    artist_of_prevote_cards: profile.artist_of_prevote_cards !== null && profile.artist_of_prevote_cards !== undefined ? [...profile.artist_of_prevote_cards] : [],
    is_wave_creator: profile.is_wave_creator === true
  };
};
export const DropAuthorBadges: React.FC<DropAuthorBadgesProps> = t0 => {
  const $ = _c(42);
  const {
    profile,
    tooltipIdPrefix: t1,
    className: t2,
    onArtistPreviewOpen,
    onWaveCreatorPreviewOpen
  } = t0;
  const tooltipIdPrefix = t1 === undefined ? "author-badges" : t1;
  const className = t2 === undefined ? DEFAULT_CONTAINER_CLASS : t2;
  const submissionCount = getSubmissionCount(profile);
  const trophyCount = getTrophyArtworkCount(profile);
  const hasActivityBadge = submissionCount > 0 || trophyCount > 0;
  const isWaveCreator = profile.is_wave_creator === true;
  let t3;
  if ($[0] !== profile) {
    t3 = toApiProfileMin(profile);
    $[0] = profile;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const modalUser = t3;
  const {
    isModalOpen: isArtistPreviewOpen,
    activeTab,
    handleBadgeClick: handleArtistBadgeClick,
    handleTabChange: handleArtistTabChange,
    handleModalClose: handleArtistModalClose
  } = useArtistPreviewModal();
  const {
    isModalOpen: isWaveCreatorPreviewOpen,
    handleBadgeClick: handleWaveCreatorBadgeClick,
    handleModalClose: handleWaveCreatorModalClose
  } = useWaveCreatorPreviewModal();
  let t4;
  if ($[2] !== handleArtistBadgeClick || $[3] !== modalUser || $[4] !== onArtistPreviewOpen) {
    t4 = tab => {
      closeAllCustomTooltips();
      if (onArtistPreviewOpen) {
        onArtistPreviewOpen({
          user: modalUser,
          initialTab: tab
        });
        return;
      }
      handleArtistBadgeClick(tab);
    };
    $[2] = handleArtistBadgeClick;
    $[3] = modalUser;
    $[4] = onArtistPreviewOpen;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const onArtistBadgeClick = t4;
  let t5;
  if ($[6] !== handleWaveCreatorBadgeClick || $[7] !== modalUser || $[8] !== onWaveCreatorPreviewOpen) {
    t5 = () => {
      closeAllCustomTooltips();
      if (onWaveCreatorPreviewOpen) {
        onWaveCreatorPreviewOpen(modalUser);
        return;
      }
      handleWaveCreatorBadgeClick();
    };
    $[6] = handleWaveCreatorBadgeClick;
    $[7] = modalUser;
    $[8] = onWaveCreatorPreviewOpen;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const onWaveCreatorBadgeClick = t5;
  if (!hasActivityBadge && !isWaveCreator) {
    return null;
  }
  let t6;
  if ($[10] !== hasActivityBadge || $[11] !== onArtistBadgeClick || $[12] !== submissionCount || $[13] !== tooltipIdPrefix || $[14] !== trophyCount) {
    t6 = hasActivityBadge && <ArtistActivityBadge submissionCount={submissionCount} trophyCount={trophyCount} onBadgeClick={onArtistBadgeClick} tooltipId={`${tooltipIdPrefix}-activity`} />;
    $[10] = hasActivityBadge;
    $[11] = onArtistBadgeClick;
    $[12] = submissionCount;
    $[13] = tooltipIdPrefix;
    $[14] = trophyCount;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== isWaveCreator || $[17] !== onWaveCreatorBadgeClick || $[18] !== tooltipIdPrefix) {
    t7 = isWaveCreator && <WaveCreatorBadge tooltipId={`${tooltipIdPrefix}-wave-creator`} onBadgeClick={onWaveCreatorBadgeClick} />;
    $[16] = isWaveCreator;
    $[17] = onWaveCreatorBadgeClick;
    $[18] = tooltipIdPrefix;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== className || $[21] !== t6 || $[22] !== t7) {
    t8 = <div className={className}>{t6}{t7}</div>;
    $[20] = className;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== activeTab || $[25] !== handleArtistModalClose || $[26] !== handleArtistTabChange || $[27] !== hasActivityBadge || $[28] !== isArtistPreviewOpen || $[29] !== modalUser || $[30] !== onArtistPreviewOpen) {
    t9 = hasActivityBadge && !onArtistPreviewOpen && <ArtistPreviewModal isOpen={isArtistPreviewOpen} onClose={handleArtistModalClose} user={modalUser} activeTab={activeTab} onTabChange={handleArtistTabChange} />;
    $[24] = activeTab;
    $[25] = handleArtistModalClose;
    $[26] = handleArtistTabChange;
    $[27] = hasActivityBadge;
    $[28] = isArtistPreviewOpen;
    $[29] = modalUser;
    $[30] = onArtistPreviewOpen;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== handleWaveCreatorModalClose || $[33] !== isWaveCreator || $[34] !== isWaveCreatorPreviewOpen || $[35] !== modalUser || $[36] !== onWaveCreatorPreviewOpen) {
    t10 = isWaveCreator && !onWaveCreatorPreviewOpen && <WaveCreatorPreviewModal isOpen={isWaveCreatorPreviewOpen} onClose={handleWaveCreatorModalClose} user={modalUser} />;
    $[32] = handleWaveCreatorModalClose;
    $[33] = isWaveCreator;
    $[34] = isWaveCreatorPreviewOpen;
    $[35] = modalUser;
    $[36] = onWaveCreatorPreviewOpen;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== t10 || $[39] !== t8 || $[40] !== t9) {
    t11 = <>{t8}{t9}{t10}</>;
    $[38] = t10;
    $[39] = t8;
    $[40] = t9;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  return t11;
};